import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Typography, Grid, Container, Breadcrumbs, Link, IconButton, CircularProgress, useTheme, Divider } from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import MainDrawer, { openDrawerWidth, collapsedDrawerWidth } from "../../MainDrawer";
import queryGraphql from "../../../helpers/ApiInterface";
import MiniBot from '../../MiniBot';
import { useAuth } from '../../../helpers/AuthProvider';

const DetailItem: React.FC<{ label: string; value?: string | number | boolean | null }> = ({ label, value }) => {
    const displayValue = value !== null && value !== undefined && value !== '' ? value : '-';
    return (
        <Box mb={2}>
            <Typography fontFamily="Gilroy-Regular" fontSize="12px" color="text.secondary">{label}</Typography>
            <Typography fontFamily="Gilroy-Semibold" fontSize="14px" sx={{ wordBreak: 'break-all' }}>{typeof displayValue === 'boolean' ? (displayValue ? 'Enabled' : 'Disabled') : displayValue}</Typography>
        </Box>
    );
};

interface SolutionOwnerData {
    id: string;
    name: string;
}

interface DataplexLakeDetails { // Changed interface name
    projectId: string;
    location: string;
    description?: string;
    // No resourceSpec or discoverySpec for a lake itself
}

interface DataLakeData { // Changed interface name
    id: string;
    name: string;
    solutionType: string;
    solutionCategoryType: string;
    reasonForRejection: string;
    cloudProvider: 'gcp' | 'aws' | 'azure' | string;
    details: {
        dataplexLake: DataplexLakeDetails; // Changed field name
    };
    status: string;
    solutionOwner: SolutionOwnerData;
}

const DataLakesDetailsPageContent: React.FC = () => { // Changed component name
    const navigate = useNavigate();
    const {id}  = useParams();
    const [dataLake, setDataLake] = useState<DataLakeData | null>(null); // Changed state variable name
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const { user } = useAuth();
    const [dynamicLabels, setDynamicLabels] = useState({
        projectId: 'Project ID',
    });

    useEffect(() => {
        const fetchDataLakeData = async () => { // Changed function name
            if (!id) {
                setIsLoading(false);
                setError("Data Lake ID is missing from URL."); // Changed text
                return;
            }

            setIsLoading(true);
            setError(null);
            try {
                const query = `
                    query GetDataplexLake($getDataplexLakeId: String!) { # Changed query name
                      getDataplexLake(id: $getDataplexLakeId) { # Changed field name
                        id
                        name
                        solutionType
                        solutionCategoryType
                        reasonForRejection
                        cloudProvider
                        status
                        details {
                          dataplexLake { # Changed from dataSources
                            projectId
                            location
                            description
                          }
                        }
                        solutionOwner {
                          id
                          name
                        }
                      }
                    }
                `;
                const response = await queryGraphql(query, { getDataplexLakeId: id }); // Changed variable name
                
                if (response.getDataplexLake) { // Changed field name
                    setDataLake(response.getDataplexLake); // Changed state variable name
                } else if (response.errors && response.errors.length > 0) {
                    setError(response.errors[0].message || "Error fetching data.");
                    setDataLake(null);
                } else {
                    setError("Data Lake not found or unexpected response structure."); // Changed text
                    setDataLake(null);
                }
            } catch (err: any) {
                console.error("Error fetching Data Lake data:", err); // Changed text
                setError(err.message || "Failed to fetch Data Lake details."); // Changed text
                setDataLake(null);
            } finally {
                setIsLoading(false);
            }
        };

        fetchDataLakeData(); // Changed function name
    }, [id]);
    
    useEffect(() => {
        if (dataLake?.cloudProvider) { // Changed state variable name
            switch (dataLake.cloudProvider) { // Changed state variable name
                case 'aws':
                    setDynamicLabels({
                        projectId: 'Account ID',
                    });
                    break;
                case 'azure':
                    setDynamicLabels({
                        projectId: 'Subscription ID',
                    });
                    break;
                case 'gcp':
                default:
                    setDynamicLabels({
                        projectId: 'Project ID',
                    });
                    break;
            }
        }
    }, [dataLake]); // Changed dependency


    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
                <CircularProgress size={24} />
                <Typography sx={{ ml: 2 }}>Loading data lake details...</Typography> {/* Changed text */}
            </Box>
        );
    }

    if (error) {
        return <Typography color="error" sx={{p:2}}>{error}</Typography>;
    }

    if (!dataLake) { // Changed state variable name
        return <Typography sx={{p:2}}>No data lake data available.</Typography>; // Changed text
    }

    const resourceOwnerName = dataLake.solutionOwner.name; // Changed state variable name
    const dlDetails = dataLake.details.dataplexLake; // Changed variable name and field

    const getStatusBackgroundColor = (status: string | undefined): string => {
        if (!status) return "#F8F8F8";
        const lowerStatus = status.toLowerCase();
        if (lowerStatus === "active" || lowerStatus === "approved") return "#12B76A10";
        if (lowerStatus === "rejected") return "#F0443810";
        if (lowerStatus === "pending") return "#FDB02210";
        return "#F8F8F8";
    };

    return (
        <Container maxWidth={false} sx={{ mt: 2, mb: 2, pl: { xs: 2, sm: 3 }, pr: {xs: 2, sm: 3} }}>
            <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2, fontSize: "14px", color: "gray" }}>
                <Link underline="hover" color="inherit" href="/datasolutions/data-lakes" sx={{fontFamily:'Gilroy-regular'}} onClick={(e) => { e.preventDefault(); navigate('/datasolutions/data-lakes'); }}> Data Lakes </Link> {/* Changed text and navigation path */}
                <Typography color="text.primary" sx={{fontSize: "14px", fontFamily:'Gilroy-Medium'}}>Data Lake Details</Typography> {/* Changed text */}
            </Breadcrumbs>

            <Box display="flex" alignItems="center" mb={3}>
                <IconButton edge="start" aria-label="back" color="inherit" onClick={() => navigate('/datasolutions/data-lakes')}> {/* Changed navigation path */}
                    <ArrowBackIosNewIcon fontSize="small" />
                </IconButton>
                <Typography variant="h5" component="h1" sx={{ fontFamily:'Gilroy-Bold',fontSize: "18px"}}> Data Lake Details </Typography> {/* Changed text */}
            </Box>

            <Box sx={{mx:0, boxShadow: "0px 3px 6px #10101029", borderRadius: "8px" }}>
                <Box sx={{
                    p: 2.5,
                    bgcolor: getStatusBackgroundColor(dataLake.status), // Changed state variable name
                    mb: 0,
                    mx: 0,
                    borderTopLeftRadius: "8px",
                    borderTopRightRadius: "8px",
                }}>
                    <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                            <Typography variant="h6" component="h2" sx={{ fontFamily: 'Gilroy-Bold', fontSize: '20px' }}>
                                {dataLake.name} {/* Changed state variable name */}
                            </Typography>
                            {user.roles?.some((e: string) => ["cluster_owner"].includes(e)) && dataLake.status === "pending" && ( // Changed state variable name
                                <IconButton sx={{ p: 0.5 }} edge="end" aria-label="edit" color="inherit" onClick={() => navigate(`/datasolutions/data-lakes/edit/${id}`)}> {/* Changed navigation path */}
                                    <img width="20px" height="20px" src="/assets/edit-icon.svg" alt="Edit"/>
                                </IconButton>
                            )}
                        </Box>
                        <Typography variant="body2" sx={{ fontFamily:'Gilroy-Regular', fontSize:'14px', color:'#666666' }}>
                            Resource Owner: {resourceOwnerName}
                        </Typography>
                    </Box>
                </Box>

                <Grid container spacing={3} sx={{p:3, pt: 2.5 }}>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>{dynamicLabels.projectId}</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dlDetails.projectId || 'N/A'}</Typography>
                    </Grid>
                     <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Location</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dlDetails.location || 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={4} md={3}>
                        <Typography variant="caption" display="block" sx={{ fontFamily:'Gilroy-Regular', fontSize:'12px', color:'#666666' }}>Cloud Provider</Typography>
                        <Typography variant="body1" sx={{ fontFamily:'Gilroy-SemiBold', fontSize:'14px', color:'#333333'}}>{dataLake.cloudProvider?.toUpperCase() || 'N/A'}</Typography> {/* Changed state variable name */}
                    </Grid>
                </Grid>
            </Box>

              {dataLake.status === 'rejected' && ( // Changed state variable name
                    <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Rejection Details</Typography>
                        <Divider sx={{ marginY: 2 }} />
                        <DetailItem label="Reason for Rejection" value={dataLake.reasonForRejection} /> {/* Changed state variable name */}
                    </Box>
                )}

                {dataLake.status === 'active' && ( // Changed state variable name
                    <Box mt={2} bgcolor="#fff" p={3} borderRadius="12px" boxShadow="0px 3px 6px #10101029">
                        <Typography fontSize="14px" fontFamily="Gilroy-SemiBold">Data Lake Specifications</Typography> {/* Changed text */}
                        <Divider sx={{ marginY: 2 }} />
                        <Grid container spacing={2.5}>
                            <Grid item xs={12}><DetailItem label="Description" value={dlDetails.description} /></Grid>
                        </Grid>
                    </Box>
                )}
        </Container>
    );
};


const DataLakesDetailsPage: React.FC = () => { // Changed component name
    const theme = useTheme();
    const getInitialDrawerState = () => {
        if (typeof window !== 'undefined') {
            const storedState = localStorage.getItem('isFaradayDrawerOpen');
            return storedState !== null ? JSON.parse(storedState) : true;
        }
        return true;
    };
    const [isDrawerActuallyOpen, setIsDrawerActuallyOpen] = useState<boolean>(getInitialDrawerState);

    const handleDrawerStateChange = (isOpenFromDrawer: boolean) => {
        setIsDrawerActuallyOpen(isOpenFromDrawer);
        if (typeof window !== 'undefined') {
            localStorage.setItem('isFaradayDrawerOpen', JSON.stringify(isOpenFromDrawer));
        }
    };

    const currentMarginLeft = isDrawerActuallyOpen ? openDrawerWidth - 248 : collapsedDrawerWidth - 70;

    return (
        <Box sx={{ display: 'flex', minHeight: '100vh' }}>
            <MainDrawer
                index={"datasolutions-data-lakes"} // Changed index
                onStateChange={handleDrawerStateChange}
            />
            <MiniBot/>
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0,
                    transition: theme.transitions.create('margin', {
                        easing: theme.transitions.easing.sharp,
                        duration: theme.transitions.duration.enteringScreen,
                    }),
                    marginLeft: {xs: 0, sm: `${currentMarginLeft}px`},
                }}
            >
                <DataLakesDetailsPageContent /> {/* Changed component */}
            </Box>
        </Box>
    );
};

export default DataLakesDetailsPage;